import { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Redeems from "./pages/Redeems";
import PublicGiveaways from "./pages/PublicGiveaways";
import PublicEvents from "./pages/PublicEvents";
import PublicSchedule from "./pages/PublicSchedule";
import AdminAppGate from "./components/AdminAppGate";
import AppErrorBoundary from "./components/AppErrorBoundary";
import Overview from "./pages/Overview";
import AI from "./pages/AI";
import Commands from "./pages/Commands";
import Currencies from "./pages/Currencies";
import Sounds from "./pages/Sounds";
import InteractionsAdmin from "./pages/InteractionsAdmin";
import Queue from "./pages/Queue";
import AdminEvents from "./pages/AdminEvents";
import OBS from "./pages/OBS";
import Settings from "./pages/Settings";
import Viewer from "./pages/Viewer";
import Overlay from "./pages/Overlay";
import Interact from "./pages/Interact";
import Leaderboard from "./pages/Leaderboard";
import LeaderboardAdmin from "./pages/LeaderboardAdmin";
import Profile from "./pages/Profile";
import Dungeons from "./pages/Dungeons";
import RPGAdmin from "./pages/RPGAdmin";
import Giveaways from "./pages/Giveaways";
import Alerts from "./pages/Alerts";
import PopupEditor from "./pages/PopupEditor";
import AutoShoutouts from "./pages/AutoShoutouts";
import Hype from "./pages/Hype";
import VIPs from "./pages/VIPs";
import AdminSchedule from "./pages/AdminSchedule";
import PlugManager from "./pages/PlugManager";
import PlatformEvents from "./pages/PlatformEvents";
import ArchitectsDownloadsOverlay from "./pages/ArchitectsDownloadsOverlay";
import CoworkingOverlay from "./pages/CoworkingOverlay";
import CoworkingAdmin from "./pages/CoworkingAdmin";

const Files = lazy(() => import("./pages/Files"));

// Globally strip the platform "Made with Emergent" badge so it never appears
// on landing, dashboard, interact, leaderboard, viewer, or overlay routes.
function useStripPlatformBadge() {
  useEffect(() => {
    const kill = () => {
      document.querySelectorAll("#emergent-badge, a[href*='emergent.sh']").forEach((el) => {
        try { el.remove(); } catch (e) { el.style.display = "none"; }
      });
    };
    const style = document.createElement("style");
    style.setAttribute("data-strip-emergent-badge", "true");
    style.textContent = `
      #emergent-badge,
      a[href*="emergent.sh"],
      [data-emergent-badge] { display: none !important; visibility: hidden !important; opacity: 0 !important; pointer-events: none !important; }
    `;
    document.head.appendChild(style);
    kill();
    const observer = new MutationObserver(kill);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { observer.disconnect(); style.remove(); };
  }, []);
}

export default function App() {
  useStripPlatformBadge();
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/links" element={<Navigate to="/events" replace />} />
        <Route path="/schedule" element={<PublicSchedule />} />
        <Route path="/events" element={<PublicEvents />} />
        <Route path="/events/:eventSlug" element={<PublicEvents />} />
        <Route path="/login" element={<Login />} />
        <Route path="/portal" element={<Navigate to="/profile" replace />} />
        <Route path="/redeems" element={<Redeems />} />
        <Route path="/giveaways" element={<PublicGiveaways />} />
        <Route path="/interact" element={<Interact />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/rpg" element={<Dungeons />} />
        <Route path="/dungeons" element={<Dungeons />} />
        <Route path="/v/:login" element={<Viewer />} />
        <Route path="/overlay" element={<Overlay />} />
        <Route path="/overlay/sounds" element={<Overlay />} />
        <Route path="/overlay/architects-downloads" element={<ArchitectsDownloadsOverlay />} />
        <Route path="/overlay/coworking" element={<CoworkingOverlay />} />
        <Route path="/overlay/coworking/timer" element={<CoworkingOverlay />} />
        <Route path="/overlay/coworking/tasks" element={<CoworkingOverlay />} />
        <Route path="/app" element={<AppErrorBoundary><AdminAppGate /></AppErrorBoundary>}>
          <Route index element={<Overview />} />
          <Route path="chat" element={<Navigate to="/app" replace />} />
          <Route path="ai" element={<AI />} />
          <Route path="commands" element={<Commands />} />
          <Route path="currencies" element={<Currencies />} />
          <Route path="interactions" element={<InteractionsAdmin />} />
          <Route path="sounds" element={<Sounds />} />
          <Route path="queue" element={<Queue />} />
          <Route path="alerts" element={<Alerts />} />
          <Route path="hype" element={<Hype />} />
          <Route path="vips" element={<VIPs />} />
          <Route path="auto-shoutouts" element={<AutoShoutouts />} />
          <Route path="popups" element={<PopupEditor />} />
          <Route path="schedule" element={<AdminSchedule />} />
          <Route path="plugs" element={<PlugManager />} />
          <Route path="platform-events" element={<PlatformEvents />} />
          <Route
            path="files"
            element={
              <Suspense fallback={<div className="p-6 text-sm text-[hsl(var(--muted-foreground))]">Loading files...</div>}>
                <Files />
              </Suspense>
            }
          />
          <Route path="giveaways" element={<Giveaways />} />
          <Route path="leaderboard" element={<LeaderboardAdmin />} />
          <Route path="rpg" element={<RPGAdmin />} />
          <Route path="events" element={<AdminEvents />} />
          <Route path="obs" element={<OBS />} />
          <Route path="coworking" element={<CoworkingAdmin />} />
          <Route path="settings" element={<Settings />} />
        </Route>
      </Routes>
      <Toaster theme="dark" position="bottom-right" richColors />
    </BrowserRouter>
  );
}
